import React, { useState, useEffect } from 'react';
import { Settings, X, Check } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import MobileDashboard from '../components/MobileDashboard';
import FutureTile from '../components/FutureTile';
import { ANALYTICS_MODULES } from "../config/analyticsModules";
import { useParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import RozoQuantLogo from "../components/TempQuantLogo";

const safeNumber = (val, decimals = 2, fallback = '—') => {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) {
    return fallback;
  }
  return Number(val).toFixed(decimals);
};


const API_BASE = '/api';

 

// Enhanced Tile Component
const LevelTile = ({ label, strike, side, color, bgGradient, strengthPct, crownPcts, normPcts, arrow, remainingIndicator }) => {
  const displayStrike = strike ? `${strike} ${side}` : '-';
  
  const compactLabels = { 
    'Vol': 'Vol', 
    'OI': 'OI', 
    'ΔW OI': 'OIΔ', 
    'OI×Vega': 'OIV', 
    'OI×Gamma': 'OIG'
  };

  const categories = ['Vol', 'OI', 'ΔW OI', 'OI×Vega', 'OI×Gamma'];
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '130px',
      padding: '10px 12px',
      background: bgGradient,
      borderRadius: '10px',
      boxShadow: `0 3px 10px ${color}40`,
      border: `2px solid ${color}`,
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        fontSize: '0.6rem',
        fontWeight: '700',
        color: '#666',
        textTransform: 'uppercase',
        marginBottom: '4px'
      }}>
        {label}
      </div>
      
      <div style={{
        fontSize: '1rem',
        fontWeight: '900',
        color: color,
        marginBottom: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        justifyContent: 'center'
      }}>
        {strike && side && arrow && (
          <span style={{
            fontSize: '0.9rem',
            color: color,
            animation: 'pulse 0.5s ease-in-out'
          }}>
            {arrow}
          </span>
        )}
        
        <span>{displayStrike}</span>
      </div>

      {strengthPct > 0 && (
        <div style={{ width: '100%', marginBottom: '6px' }}>
          <div style={{
            width: '100%',
            height: '3px',
            background: 'rgba(0,0,0,0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${strengthPct}%`,
              height: '100%',
              background: color,
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{
            fontSize: '0.65rem',
            fontWeight: '700',
            color: color,
            textAlign: 'center',
            marginTop: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}>
            <span>{strengthPct}%</span>
            {remainingIndicator && (
              <span style={{
                fontSize: '0.50rem',
                fontWeight: '1000',
                color: '#ff9800',
                background: 'rgba(255,152,0,0.15)',
                padding: '1px 4px',
                borderRadius: '3px'
              }}>
                {remainingIndicator}
              </span>
            )}
          </div>
        </div>
      )}
      
      {crownPcts && normPcts && Object.keys(crownPcts).length > 0 && (
        <div style={{
          width: '100%',
          fontSize: '0.65rem',
          marginTop: '4px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '2px',
            paddingBottom: '2px',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}>
            {categories.map(cat => (
              <div key={cat} style={{
                flex: 1,
                textAlign: 'center',
                fontWeight: '800',
                color: '#78909c'
              }}>
                {compactLabels[cat]}
              </div>
            ))}
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '1px'
          }}>
            {categories.map(cat => {
              const pct = crownPcts[cat] || 0;
              const crownColor = pct >= 90 ? '#00c853' : pct >= 60 ? '#ff9800' : '#f44336';
              return (
                <div key={cat} style={{
                  flex: 1,
                  textAlign: 'center',
                  fontWeight: '800',
                  color: crownColor
                }}>
                  {pct}
                </div>
              );
            })}
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            {categories.map(cat => {
              const pct = normPcts[cat] || 0;
              const normColor = pct >= 90 ? '#4fc3f7' : '#90a4ae';
              return (
                <div key={cat} style={{
                  flex: 1,
                  textAlign: 'center',
                  fontWeight: '700',
                  color: normColor,
                  opacity: 0.8
                }}>
                  {pct}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


const UnderlyingTile = ({ name, price, loading, obOs, expiry, liveSpotData, biasData, spotChangePct, directionPredictor }) => {

  const displayPrice = liveSpotData?.spot?.ltp ?? price ?? 0;
  const ohlc = liveSpotData?.spot?.ohlc;
  
  const [showTooltip, setShowTooltip] = useState(false);
  const [showBiasTooltip, setShowBiasTooltip] = useState(false);
  const [showPredictorTooltip, setShowPredictorTooltip] = useState(false);
  
  // Direction Predictor HTML
  let predictorHtml = null;
  if (directionPredictor) {
    const { strike, bar_count, duration_min, direction_arrow } = directionPredictor;
    
    const arrowColor = direction_arrow === "⬆️" ? '#00c853' : 
                      direction_arrow === "⬇️" ? '#ff1744' : '#78909c';
    
    // Bar rendering - Progressive heights (signal style)
    const renderBars = () => {
      // Define 3 bar heights (small → medium → large)
      const barHeights = ['8px', '12px', '16px'];
      const barWidth = '6px';  // Same width for all bars
      
      return (
        <div style={{
          display: 'flex',
          gap: '3px',
          alignItems: 'flex-end'
        }}>
          {[...Array(bar_count)].map((_, i) => (
            <div
              key={i}
              style={{
                width: barWidth,
                height: barHeights[i],  // Progressive: 8px → 12px → 16px
                background: arrowColor,
                borderRadius: '2px'
              }}
            />
          ))}
        </div>
      );
    };

    
    predictorHtml = (
      <div 
        style={{
          marginTop: '6px',
          padding: '6px 10px',
          background: 'rgba(33,150,243,0.1)',
          borderRadius: '8px',
          border: '1.5px solid #2196f3',
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={() => setShowPredictorTooltip(true)}
        onMouseLeave={() => setShowPredictorTooltip(false)}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'center'
        }}>
          {/* Arrow */}
          <div style={{
            fontSize: '1rem',
            color: arrowColor
          }}>
            {direction_arrow}
          </div>
          
          {/* Strike */}
          <div style={{
            fontSize: '0.85rem',
            fontWeight: '900',
            color: '#2196f3'
          }}>
            {strike}
          </div>
          
          {/* Bar Signal */}
          {renderBars()}
          
          {/* Duration */}
          <div style={{
            fontSize: '0.65rem',
            fontWeight: '700',
            color: '#666'
          }}>
            {duration_min}m
          </div>
        </div>
        
        {/* Tooltip */}
        {showPredictorTooltip && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0,0,0,0.9)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '6px',
            minWidth: '180px'
          }}>
            <div style={{ 
              marginBottom: '6px', 
              fontWeight: '800', 
              color: '#2196f3',
              textAlign: 'center'
            }}>
              🎯 Direction Predictor
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'auto auto', 
              gap: '4px 12px', 
              textAlign: 'left' 
            }}>
              <span>Target Strike:</span>
              <span style={{ color: '#2196f3', fontWeight: 800 }}>{strike}</span>
              
              <span>Consensus:</span>
              <span style={{ color: '#00c853', fontWeight: 700 }}>
                {bar_count === 3 ? '100%' : bar_count === 2 ? '80%' : '60%'}
              </span>
              
              <span>Duration:</span>
              <span style={{ color: '#ff9800', fontWeight: 700 }}>{duration_min}m</span>
            </div>
            <div style={{
              marginTop: '6px',
              fontSize: '0.55rem',
              color: '#aaa',
              fontStyle: 'italic',
              textAlign: 'center',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              paddingTop: '4px'
            }}>
              {bar_count === 3 ? '5/5 leaders aligned' : 
              bar_count === 2 ? '4/5 leaders aligned' : 
              '3/5 leaders aligned'}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // OB/OS HTML
  let obOsHtml = null;
  if (obOs) {
    const isOB = obOs.startsWith('OB');
    const textColor = isOB ? '#d50000' : '#00695c';
    const bgColor = isOB ? 'rgba(255,23,68,0.2)' : 'rgba(0,200,83,0.2)';
    const icon = isOB ? '🔥' : '❄️';
    
    obOsHtml = (
      <div style={{
        marginTop: '4px',
        padding: '2px 6px',
        background: bgColor,
        borderRadius: '6px',
        border: `1px solid ${textColor}`,
        fontSize: '0.6rem',
        fontWeight: '800',
        color: textColor
      }}>
        {icon} {obOs}
      </div>
    );
  }
  
  // Bias HTML
  let biasHtml = null;
  if (biasData && typeof biasData.bias === 'number' && typeof biasData.change_pct === 'number') {
    const { bias, change_pct, signal } = biasData;
    
    let biasColor, biasBg, biasIcon;
    
    if (signal === 'BUY_CALL') {
      biasColor = '#00c853';
      biasBg = 'rgba(0,200,83,0.2)';
      biasIcon = '🟢';
    } else if (signal === 'BUY_PUT') {
      biasColor = '#ff1744';
      biasBg = 'rgba(255,23,68,0.2)';
      biasIcon = '🔴';
    } else {
      biasColor = '#78909c';
      biasBg = 'rgba(120,144,156,0.15)';
      biasIcon = '⚫';
    }
    
    const changeColor = change_pct > 0 ? '#4CAF50' : (change_pct < 0 ? '#f44336' : '#666');
    
    biasHtml = (
      <div 
        style={{
          marginTop: '6px',
          padding: '4px 8px',
          background: biasBg,
          borderRadius: '8px',
          border: `1.5px solid ${biasColor}`,
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={() => setShowBiasTooltip(true)}
        onMouseLeave={() => setShowBiasTooltip(false)}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'center'
        }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: '900',
            color: biasColor
          }}>
            {biasIcon}
          </div>

          <div style={{
            fontSize: '0.6rem',
            fontWeight: '700',
            color: '#666',
            textTransform: 'uppercase'
          }}>
            Bias
          </div>

          <div style={{
            fontSize: '0.75rem',
            fontWeight: '900',
            color: biasColor
          }}>
            {safeNumber(bias, 3)}
          </div>

          <div style={{
            fontSize: '0.6rem',
            fontWeight: '700',
            color: changeColor
          }}>
            {change_pct > 0 ? '+' : ''}{safeNumber(change_pct, 1)}%
          </div>
        </div>

        {biasData?.regime && (
          <div style={{
            marginTop: '4px',
            fontSize: '0.55rem',
            fontWeight: '700',
            color: '#444',
            textAlign: 'center',
            letterSpacing: '0.3px'
          }}>
            {/* Regime : {biasData.regime.replaceAll('_', ' ')} */}
          </div>
        )}
        
        {showBiasTooltip && biasData.final_score && biasData.pressure_score && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0,0,0,0.9)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', textAlign: 'left' }}>
              <span>Final:</span><span style={{ color: '#2196f3', fontWeight: 800 }}>{safeNumber(biasData.final_score, 4)}</span>
              <span>Pressure:</span><span style={{ color: '#ff9800', fontWeight: 700 }}>{safeNumber(biasData.pressure_score, 4)}</span>
              <span>Change:</span><span style={{ color: changeColor, fontWeight: 700 }}>{change_pct > 0 ? '+' : ''}{safeNumber(change_pct, 1)}%</span>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '140px',
      padding: '8px 14px',
      background: 'linear-gradient(180deg, #e3f2fd, #bbdefb)',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
      border: '2px solid #1976d2',
      transition: 'all 0.3s ease',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        fontSize: '0.7rem',
        fontWeight: '700',
        color: '#1565c0',
        textTransform: 'uppercase',
        marginBottom: '4px'
      }}>
        {name}
      </div>
      
      {/* Expiry */}
      {expiry && (
        <div style={{
          fontSize: '0.55rem',
          fontWeight: '600',
          color: '#1976d2',
          marginBottom: '4px',
          opacity: 0.8
        }}>
          {expiry}
        </div>
      )}
      
      {/* ✅ Direction Predictor - ABOVE LTP */}
      {predictorHtml}
    

      {/* LTP with OHLC tooltip */}
      <div 
        style={{
          fontSize: '1.3rem',
          fontWeight: '900',
          color: '#1976d2',
          textAlign: 'center',
          marginTop: '6px',
          cursor: (ohlc && ohlc.open && ohlc.high && ohlc.low && ohlc.close) ? 'pointer' : 'default',
          position: 'relative'
        }}
        onMouseEnter={() => (ohlc && ohlc.open) && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {loading ? '⏳' : `₹${safeNumber(displayPrice)}`}
        
        {/* OHLC Tooltip */}
        {ohlc && ohlc.open && ohlc.high && ohlc.low && ohlc.close && showTooltip && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0,0,0,0.9)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', textAlign: 'left' }}>
              <span>Open:</span><span style={{ color: '#2196f3', fontWeight: 800 }}>₹{safeNumber(ohlc.open)}</span>
              <span>High:</span><span style={{ color: '#4CAF50', fontWeight: 700 }}>₹{safeNumber(ohlc.high)}</span>
              <span>Low:</span><span style={{ color: '#f44336', fontWeight: 700 }}>₹{safeNumber(ohlc.low)}</span>
              <span>Prev Close:</span><span style={{ color: '#ff9800', fontWeight: 700 }}>₹{safeNumber(ohlc.close)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Spot Change % */}
      {typeof spotChangePct === 'number' && (
        <div style={{
          marginTop: '2px',
          fontSize: '0.65rem',
          fontWeight: '800',
          color: spotChangePct > 0 ? '#4CAF50' : (spotChangePct < 0 ? '#f44336' : '#666')
        }}>
          {spotChangePct > 0 ? '▲' : spotChangePct < 0 ? '▼' : ''}
          {spotChangePct > 0 ? '+' : ''}{safeNumber(spotChangePct)}%
        </div>
      )}

      {/* OB/OS Display */}
      {obOsHtml}
      
      {/* Bias Display */}
      {biasHtml}
    </div>
  );
};


const OptionLTPTile = ({ name, optionsData, loading }) => {
  const [showTooltip, setShowTooltip] = useState({});
  
  const isEmpty = (obj) =>
    !obj || Object.values(obj).every(v => !v || Object.keys(v).length === 0);

  if (isEmpty(optionsData)) {
    return null;
  }
  
  const getChangeColor = (change) => {
    return change > 0 ? '#4CAF50' : (change < 0 ? '#f44336' : '#666');
  };
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      minWidth: '180px',
      padding: '8px 6px',
      background: 'linear-gradient(180deg, #f3e5f5, #e1bee7)',
      borderRadius: '1px',
      boxShadow: '0 4px 12px rgba(156,39,176,0.25)',
      border: '2px solid #9c27b0',
      gap: '1px'
    }}>
      <div style={{
        fontSize: '0.7rem',
        fontWeight: '700',
        color: '#6a1b9a',
        textTransform: 'uppercase',
        textAlign: 'center'
      }}>
      </div>
      
      {[ 'OTM','ATM', 'ITM'].map(label => {
        let data = {};

        if (label === 'ATM') {
          data = optionsData['ATM'] || {};
        }

        else if (label === 'ITM') {
          // ITM = ATM-1 CE + ATM+1 PE
          const itmCE = optionsData['ATM-1']?.CE;
          const itmPE = optionsData['ATM+1']?.PE;
          if (itmCE) data.CE = itmCE;
          if (itmPE) data.PE = itmPE;
        }

        else if (label === 'OTM') {
          // OTM = ATM+1 CE + ATM-1 PE
          const otmCE = optionsData['ATM+1']?.CE;
          const otmPE = optionsData['ATM-1']?.PE;
          if (otmCE) data.CE = otmCE;
          if (otmPE) data.PE = otmPE;
        }


        if (!data || Object.keys(data).length === 0) return null;

        return (
          <div key={label} style={{
            background: 'rgba(255,255,255,0.7)',
            padding: '6px 6px',
            borderRadius: '6px',
            border: '1px solid #ba68c8'
          }}>
            <div style={{
              fontSize: '0.65rem',
              fontWeight: '700',
              color: '#7b1fa2',
              marginBottom: '4px',
              textAlign: 'center'
            }}>
              {label}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '6px',
              justifyItems: 'center'
            }}>
              {['CE', 'PE'].map(type => {
                const opt = data[type];
                if (!opt) return null;

                const changeColor = getChangeColor(opt.change_pct);
                const tooltipKey = `${label}_${type}`;
                
                // ✅ EXTRACT OHLC DATA
                const ohlc = opt.ohlc || {};
                const hasOHLC = ohlc.high && ohlc.low;

                return (
                  <div
                    key={type}
                    style={{
                      padding: '4px 6px',
                      background: type === 'CE' ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)',
                      borderRadius: '4px',
                      border: `1px solid ${type === 'CE' ? '#4CAF50' : '#f44336'}`,
                      position: 'relative',
                      cursor: hasOHLC ? 'pointer' : 'default',
                      textAlign: 'center',
                      minWidth: '110px',
                      maxWidth: '110px',  
                      width: '110px'                  
                    }}
                    onMouseEnter={() =>
                      setShowTooltip(prev => ({ ...prev, [tooltipKey]: true }))
                    }

                    onMouseLeave={() =>
                      setShowTooltip(prev => ({ ...prev, [tooltipKey]: false }))
                    }

                  >
                    <div style={{
                      fontSize: '0.55rem',
                      fontWeight: '700',
                      color: type === 'CE' ? '#2e7d32' : '#c62828',
                      marginBottom: '2px'
                    }}>
                      {opt.strike} {type}
                    </div>

                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: '800',
                      color: '#333'
                    }}>
                      ₹{safeNumber(opt.ltp)}

                    </div>


                    {(typeof opt.change_pct_5min === 'number' || typeof opt.change_pct_15min === 'number') && (
                      <div style={{
                        fontSize: '0.6rem',
                        fontWeight: '700',
                        marginTop: '2px',
                        display: 'flex',
                        gap: '4px',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        {/* 5min change */}
                        <span style={{
                          color: getChangeColor(opt.change_pct_5min || 0),
                          opacity: (opt.change_pct_5min || 0) === 0 ? 0.6 : 1
                        }}>
                          {(opt.change_pct_5min || 0) > 0 ? '+' : ''}
                          {safeNumber(opt.change_pct_5min || 0, 1)}%
                        </span>
                        
                        {/* Separator */}
                        <span style={{ color: '#999', fontSize: '0.5rem' }}>|</span>
                        
                        {/* 15min change */}
                        <span style={{
                          color: getChangeColor(opt.change_pct_15min || 0),
                          opacity: (opt.change_pct_15min || 0) === 0 ? 0.6 : 1,
                          fontSize: '0.55rem'
                        }}>
                          {(opt.change_pct_15min || 0) > 0 ? '+' : ''}
                          {safeNumber(opt.change_pct_15min || 0, 1)}%
                        </span>
                        
                        {/* ✅ ADD: Average */}
                        <span style={{ color: '#999', fontSize: '0.5rem' }}>|</span>
                        
                        <span style={{
                          color: getChangeColor(opt.change_pct_avg || 0),
                          opacity: (opt.change_pct_avg || 0) === 0 ? 0.6 : 1,
                          fontSize: '0.65rem',  // Slightly larger to emphasize
                          fontWeight: '800'
                        }}>
                          {(opt.change_pct_avg || 0) > 0 ? '+' : ''}
                          {safeNumber(opt.change_pct_avg || 0, 1)}%
                        </span>
                      </div>
                    )}



                    {/* ✅ MODIFIED TOOLTIP - SHOW HIGH/LOW */}
                    {showTooltip[tooltipKey] && hasOHLC && (
                      <div style={{
                        position: 'absolute',
                        background: 'rgba(0,0,0,0.9)',
                        color: '#fff',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '0.6rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginTop: '6px',
                        pointerEvents: 'none' 
                      }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'auto auto', 
                          gap: '4px 12px', 
                          textAlign: 'left' 
                        }}>
                          <span>{label} {type} ({opt.strike})</span>
                          <span></span>
                          <span>Day High:</span>
                          <span style={{ color: '#4CAF50', fontWeight: 800 }}>
                            ₹{safeNumber(ohlc.high)}

                          </span>
                          <span>Day Low:</span>
                          <span style={{ color: '#f44336', fontWeight: 800 }}>
                            ₹{safeNumber(ohlc.low)}

                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// PCR Analysis Component
const PCRAnalysisRow = ({ pcrData }) => {
  if (!pcrData || pcrData.length === 0) {
    return null;
  }
  
  const getPCRColor = (level) => {
    const colors = {
      'very_bearish': { bg: 'rgba(76,175,80,0.2)', text: '#2e7d32', border: '#4caf50' },
      'bearish': { bg: 'rgba(139,195,74,0.15)', text: '#558b2f', border: '#8bc34a' },
      'neutral': { bg: 'rgba(255,193,7,0.1)', text: '#f57c00', border: '#ffc107' },
      'bullish': { bg: 'rgba(255,152,0,0.15)', text: '#e65100', border: '#ff9800' },
      'very_bullish': { bg: 'rgba(244,67,54,0.2)', text: '#c62828', border: '#f44336' }
    };
    return colors[level] || colors.neutral;
  };
  
  return (
    <div style={{
      marginTop: '12px',
      padding: '12px',
      background: 'linear-gradient(135deg, #f5f5f5, #fafafa)',
      borderRadius: '8px',
      border: '1px solid #e0e0e0'
    }}>
      <div style={{
        fontSize: '0.7rem',
        fontWeight: '700',
        color: '#666',
        marginBottom: '8px',
        textAlign: 'center'
      }}>
        📊 PCR & Net Gamma Analysis (ATM±5)
      </div>
      
      <div style={{
        display: 'flex',
        gap: '6px',
        justifyContent: 'flex-start',
        overflowX: 'auto',
        padding: '4px'
      }}>
        {pcrData.map((item, idx) => {
          const colors = getPCRColor(item.pcr_level);
          const showAlert = item.crosses_threshold;
          const gammaColor = item.net_gamma > 0 ? '#00c853' : '#f44336';
          
          return (
            <div key={idx} style={{
              minWidth: '95px',
              padding: '8px 6px',
              background: colors.bg,
              borderRadius: '6px',
              border: showAlert ? `2px solid ${colors.border}` : `1px solid ${colors.border}`,
              boxShadow: showAlert ? `0 0 8px ${colors.border}` : 'none',
              transition: 'all 0.3s ease'
            }}>
              {/* Strike + ATM Label */}
              <div style={{
                fontSize: '0.75rem',
                fontWeight: item.is_atm ? '900' : '600',
                color: item.is_atm ? '#f57f17' : '#666',
                textAlign: 'center',
                marginBottom: '3px',
                background: item.is_atm ? '#fff9c4' : 'transparent',
                padding: '2px',
                borderRadius: '3px'
              }}>
                {item.atm_label}
              </div>
              
              <div style={{
                fontSize: '0.85rem',
                fontWeight: '700',
                color: '#333',
                textAlign: 'center',
                marginBottom: '6px'
              }}>
                {item.strike}
              </div>
              
              {/* PCR Section */}
              <div style={{
                padding: '4px',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '4px',
                marginBottom: '4px'
              }}>
                <div style={{
                  fontSize: '0.55rem',
                  color: '#666',
                  textAlign: 'center',
                  marginBottom: '2px'
                }}>
                  PCR
                </div>
                
                <div style={{
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  color: colors.text,
                  textAlign: 'center',
                  marginBottom: '2px'
                }}>
                  {item.pcr_value.toFixed(2)}
                </div>
                
                {typeof item.change_pct === 'number' && (
                  <div style={{
                    fontSize: showAlert ? '0.7rem' : '0.65rem',
                    fontWeight: showAlert ? '800' : '600',
                    color: item.change_pct > 0 ? '#4CAF50' : '#f44336',
                    textAlign: 'center',
                    opacity: item.change_pct === 0 ? 0.6 : 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <span>
                      {item.change_pct > 0 ? '+' : ''}
                      {item.change_pct.toFixed(1)}%
                    </span>
                    {item.strong_change && <span>🔥</span>}
                    {!item.strong_change && item.crosses_threshold && <span>⚡</span>}
                  </div>
                )}
              </div>
              
              {/* Net Gamma Section */}
              <div style={{
                padding: '4px',
                background: 'rgba(156,39,176,0.1)',
                borderRadius: '4px',
                border: '1px solid rgba(156,39,176,0.3)'
              }}>
                <div style={{
                  fontSize: '0.55rem',
                  color: '#666',
                  textAlign: 'center',
                  marginBottom: '2px'
                }}>
                  Net Γ
                </div>
                
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '800',
                  color: gammaColor,
                  textAlign: 'center',
                  marginBottom: '2px'
                }}>
                  {item.net_gamma > 0 ? '+' : ''}{item.net_gamma.toLocaleString()}
                </div>
                
                {typeof item.gamma_change_pct === 'number' && (
                  <div style={{
                    fontSize: item.gamma_crosses_threshold ? '0.7rem' : '0.65rem',
                    fontWeight: item.gamma_crosses_threshold ? '800' : '600',
                    color: item.gamma_change_pct > 0 ? '#4CAF50' : '#f44336',
                    textAlign: 'center',
                    opacity: item.gamma_change_pct === 0 ? 0.6 : 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '2px'
                  }}>
                    <span>
                      {item.gamma_change_pct > 0 ? '+' : ''}
                      {item.gamma_change_pct.toFixed(1)}%
                    </span>
                    {item.gamma_strong_change && <span>🔥</span>}
                    {!item.gamma_strong_change && item.gamma_crosses_threshold && <span>⚡</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div style={{
        marginTop: '8px',
        fontSize: '0.6rem',
        color: '#888',
        textAlign: 'center'
      }}>
        🔥 = Change ≥15% | ⚡ = Change ≥10% | 5min tracking
      </div>
    </div>
  );
};


// Top builders and regime Analysis Component

// ============================================================
// MODIFIED TopBuildersTile component — drop-in replacement
// Changes:
//   1. Duration = rank-based (resets when rank occupant changes)
//   2. Rank Rotation & Expansion — show signal ONLY (no strike/side), persistent
//   3. Target column — strike+side based on signal direction vs top3 builders ≥60%
// ============================================================

const TopBuildersTile = ({ buildersData, regimeAnalysis, spotOhlc, spotPrice, netExposures }) => {
  if (
    !buildersData ||
    (!buildersData.oi_builders?.length && !buildersData.vol_builders?.length)
  ) {
    return null;
  }

  const { oi_builders = [], vol_builders = [] } = buildersData;

  const getMomentumMeta = (v = 0) => {
    if (v > 0.3) return { icon: '▲', color: '#4CAF50' };
    if (v < -0.3) return { icon: '▼', color: '#f44336' };
    return { icon: '•', color: '#aaa' };
  };


  // ----------------------------------------------------------------
  // Rank Rotation column — signal ONLY (persistent from backend)
  // ----------------------------------------------------------------
  const renderRankRotation = (regimeDays) => {
    const d = regimeDays?.intraday;
    if (!d?.rank_change) {
      return <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>–</div>;
    }
    const isUp = d.rank_change === 'UP';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{
          fontSize: '1.1rem',
          fontWeight: '900',
          color: isUp ? '#00c853' : '#f44336',
          lineHeight: 1
        }}>
          {isUp ? '▲' : '▼'}
        </span>
      </div>
    );
  };

  // ----------------------------------------------------------------
  // Expansion column — signal ONLY (persistent from backend)
  // ----------------------------------------------------------------
  const renderExpansion = (regimeDays) => {
    const d = regimeDays?.intraday;
    if (!d?.expansion) {
      return <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>–</div>;
    }
    const isUp = d.expansion === 'UP';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{
          fontSize: '1.1rem',
          fontWeight: '900',
          color: isUp ? '#00c853' : '#f44336',
          lineHeight: 1
        }}>
          {isUp ? '▲' : '▼'}
        </span>
      </div>
    );
  };

  const renderTrend = (regimeDays) => {
    const trend = regimeDays?.intraday?.builder_trend;
    if (!trend) return <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>–</div>;
    const isUp = trend === 'UP';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '1.1rem', fontWeight: '900', color: isUp ? '#00c853' : '#f44336', lineHeight: 1 }}>
          {isUp ? '▲' : '▼'}
        </span>
      </div>
    );
  };

  // ----------------------------------------------------------------
  // Check if a strike has been touched by today's price action
  // ----------------------------------------------------------------
  const hasStrikeBeenTouched = (strike) => {
    if (!spotOhlc || !strike) return false;
    const high = spotOhlc.high || 0;
    const low = spotOhlc.low || 0;
    return strike >= low && strike <= high;
  };

  // ----------------------------------------------------------------
  // Target column — derived from signal + top3 builders with ≥60%
  // If UP signal → highest strike among top3 with relative_pct ≥ 60
  // If DOWN signal → lowest strike among top3 with relative_pct ≥ 60
  // If no qualifying strike → display "–"
  // ----------------------------------------------------------------
  const renderTarget = (regimeDays, builderData) => {
    const d = regimeDays?.intraday;

    // Determine signal: rank_change takes priority; fall back to expansion
    const signal = d?.rank_change || d?.expansion;

    if (!signal || !builderData || builderData.length === 0) {
      return <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>–</div>;
    }

    // Only top 3 are candidates
    const top3 = builderData.slice(0, 3);
    const qualified = top3.filter(b => (b.relative_pct ?? 0) >= 60);

    if (qualified.length === 0) {
      return <div style={{ textAlign: 'center', color: '#bbb', fontSize: '0.8rem' }}>–</div>;
    }

    let target;
    if (signal === 'UP') {
      target = qualified.reduce((best, b) => b.strike > best.strike ? b : best, qualified[0]);
    } else {
      target = qualified.reduce((best, b) => b.strike < best.strike ? b : best, qualified[0]);
    }

  const targetColor = target.side === 'CE' ? '#f44336' : '#4CAF50';
      const touched = hasStrikeBeenTouched(target.strike);

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1px'
        }}>
          <span style={{
            fontSize: '0.72rem',
            fontWeight: '900',
            color: targetColor,
            textAlign: 'center'
          }}>

          {touched ? '⚪' : target.side === 'CE' ? '🟢' : '🔴'} {target.strike} {target.side}
          </span>
        </div>
      );
  };

  // ----------------------------------------------------------------
  // D-1/D-2/D-3 — strike + side ONLY
  // ----------------------------------------------------------------
  const renderRegimeDay = (categoryData) => {
    if (!categoryData || !categoryData.target_strike) {
      return <div style={{ fontSize: '0.7rem', color: '#bbb', textAlign: 'center' }}>–</div>;
    }
    const { target_strike, target_side } = categoryData;
    return (
      <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#333', textAlign: 'center' }}>
        {target_strike} {target_side}
      </div>
    );
  };

  // ── LEVEL STRENGTH CALCULATOR ─────────────────────────────────────────────
  // Weights: Rank1 = 20pts, Rank2 = 10pts, Rank3 = 5pts per category
  const computeLevelStrengths = (cats) => {
    const scores = {};
    cats.forEach(({ builderData }) => {
      if (!builderData) return;
      builderData.slice(0, 3).forEach((b, idx) => {
        const pts = idx === 0 ? 20 : idx === 1 ? 10 : 5;
        const key = `${b.strike} ${b.side}`;
        scores[key] = (scores[key] || 0) + pts;
      });
    });
    // Return only entries >= 60%, sorted descending
    return Object.entries(scores)
      .filter(([, pct]) => pct >= 60)
      .sort(([, a], [, b]) => b - a);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const categories = [
    { key: 'volume',   label: '📊 Vol',   builderData: vol_builders,                   type: 'VOL',      regimeKey: 'volume'   },
    { key: 'oi_delta', label: '⚡ OI×Δ', builderData: buildersData.oi_delta_builders, type: 'OI_DELTA', regimeKey: 'oi_delta' },
    { key: 'oi_vega',  label: '💎 OI×V', builderData: buildersData.oi_vega_builders,  type: 'OI_VEGA',  regimeKey: 'oi_vega'  },
    { key: 'oi_gamma', label: '🎯 OI×Γ', builderData: buildersData.oi_gamma_builders, type: 'OI_GAMMA', regimeKey: 'oi_gamma' },
    { key: 'oi',       label: '🔥 OI',    builderData: oi_builders,                    type: 'OI',       regimeKey: 'oi'       },
  ];

  // ── Closure Bracket Computation ──────────────────────────────────────────────
  const computeClosureBracket = () => {
    const categoryMins = [];
    const categoryMaxes = [];

    categories.forEach(({ builderData }) => {
      if (!builderData || builderData.length === 0) return;

      // Step 1: Start with top-3, expand to top-5 if needed to get both sides
      let window = builderData.slice(0, 3);
      let hasCE = window.some(b => b.side === 'CE');
      let hasPE = window.some(b => b.side === 'PE');

      if (!hasCE || !hasPE) {
        window = builderData.slice(0, 4);
        hasCE = window.some(b => b.side === 'CE');
        hasPE = window.some(b => b.side === 'PE');
      }
      if (!hasCE || !hasPE) {
        window = builderData.slice(0, 5);
        hasCE = window.some(b => b.side === 'CE');
        hasPE = window.some(b => b.side === 'PE');
      }

      // Use top-3 strikes only for bracket math (per spec §2)
      const strikes = window.map(b => b.strike).filter(Boolean);
      if (strikes.length === 0) return;

      categoryMins.push({ val: Math.min(...strikes), balanced: hasCE && hasPE });
      categoryMaxes.push({ val: Math.max(...strikes), balanced: hasCE && hasPE });
    });

    if (categoryMins.length === 0) return null;

    // Lower bound = MAX of all category_mins (PE side — lowest strike across categories)
    // Upper bound = MIN of all category_maxes (CE side — highest strike across categories)
    const lowerBound = Math.max(...categoryMins.map(x => x.val));
    const upperBound = Math.min(...categoryMaxes.map(x => x.val));
    const isValid = lowerBound <= upperBound;

    // Find which category builder gives the PE strike at lowerBound and CE at upperBound
    let lbSide = null, ubSide = null;
    categories.forEach(({ builderData }) => {
      if (!builderData) return;
      const top5 = builderData.slice(0, 5);
      top5.forEach(b => {
        if (b.strike === lowerBound && b.side === 'PE') lbSide = 'PE';
        if (b.strike === upperBound && b.side === 'CE') ubSide = 'CE';
      });
    });

    return { lowerBound, upperBound, isValid, lbSide: lbSide || 'PE', ubSide: ubSide || 'CE' };
  };

  const closureBracket = computeClosureBracket();
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      borderRadius: '10px',
      padding: '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid rgba(0,0,0,0.1)',
      marginTop: '16px'
    }}>
      <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#e65100', marginBottom: '10px', textAlign: 'center' }}>
        🔥 Top Builders & Regime Analysis
      </div>

      {/* ── Level Strength Bar ── */}
      {(() => {
        const strengths = computeLevelStrengths(categories);
        if (strengths.length === 0) return null;
        return (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '8px',
            padding: '6px 8px',
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            border: '1px solid rgba(0,0,0,0.08)'
          }}>
            {strengths.map(([level, pct]) => {
              const strike = parseInt(level.split(' ')[0]);
              const touched = hasStrikeBeenTouched(strike);
              const spot = spotPrice || 0;
              const distance = strike - spot;
              const distAbs = Math.round(Math.abs(distance));
              const isUp = distance > 0;
              return (
                <span key={level} style={{
                  fontSize: '0.72rem',
                  fontWeight: '800',
                  color: touched ? '#555' : '#1b5e20',
                  background: touched ? '#f5f5f5' : '#e8f5e9',
                  border: `1px solid ${touched ? '#bdbdbd' : '#a5d6a7'}`,
                  borderRadius: '6px',
                  padding: '2px 8px',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {touched ? '⚪' : '🟢'} {level} &nbsp; {pct}%
                  <span style={{ color: isUp ? '#4CAF50' : '#f44336', fontWeight: '900' }}>
                    {isUp ? '▲' : '▼'} {isUp ? '+' : '-'}{distAbs}
                  </span>
                </span>
              );
            })}
          </div>
        );
      })()}


        {/* ── Closure Bracket Row ── */}
        {closureBracket && (() => {
          const { lowerBound, upperBound, isValid, lbSide, ubSide } = closureBracket;
          return (
            <React.Fragment>
              {/* Category cell */}
              <div style={{
                fontSize: '0.58rem', fontWeight: '800',
                color: '#37474f',
                lineHeight: '1.3',
                padding: '4px 2px'
              }}>
              {/*  📐 Bracket */}
              </div>

              {/* Span across builder cols + regime cols — full width bracket display */}
              <div style={{
                gridColumn: '2 / -1',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '5px 10px',
                background: isValid
                  ? 'linear-gradient(90deg, rgba(76,175,80,0.12), rgba(33,150,243,0.12))'
                  : 'rgba(244,67,54,0.08)',
                borderRadius: '6px',
                border: isValid
                  ? '1px solid rgba(76,175,80,0.4)'
                  : '1px solid rgba(244,67,54,0.3)',
                flexWrap: 'wrap'
              }}>
                {isValid ? (
                  <>
                    {/* Lower bound — PE side */}
                    <span style={{
                      fontSize: '0.72rem', fontWeight: '900',
                      color: '#2e7d32',
                      background: 'rgba(76,175,80,0.15)',
                      border: '1px solid #a5d6a7',
                      borderRadius: '5px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap'
                    }}>
                    {hasStrikeBeenTouched(lowerBound) ? '⚪' : '🔴'} {lowerBound} {lbSide}
                    </span>

                    <span style={{ fontSize: '0.65rem', color: '#78909c', fontWeight: '700' }}>
                      Expected Closure
                    </span>

                    {/* Width indicator */}
                    <span style={{
                      fontSize: '0.65rem', fontWeight: '700',
                      color: '#546e7a',
                      background: 'rgba(255,255,255,0.7)',
                      borderRadius: '4px',
                      padding: '2px 6px'
                    }}>
                      ↔ {upperBound - lowerBound} pts
                    </span>

                    <span style={{ fontSize: '0.65rem', color: '#78909c', fontWeight: '700' }}>
                      Expected Closure
                    </span>

                    {/* Upper bound — CE side */}
                    <span style={{
                      fontSize: '0.72rem', fontWeight: '900',
                      color: '#1565c0',
                      background: 'rgba(33,150,243,0.15)',
                      border: '1px solid #90caf9',
                      borderRadius: '5px',
                      padding: '2px 8px',
                      whiteSpace: 'nowrap'
                    }}>
                    {hasStrikeBeenTouched(upperBound) ? '⚪' : '🟢'} {upperBound} {ubSide}
                    </span>
                  </>
                ) : (
                  <span style={{
                    fontSize: '0.68rem', fontWeight: '800',
                    color: '#c62828',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    ⚠️ No Valid Bracket — Structural Disagreement
                    <span style={{ fontWeight: '500', color: '#e57373', fontSize: '0.62rem' }}>
                      ({lowerBound} PE ≥ {upperBound} CE)
                    </span>
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })()}


      {/* Net Exposures Row */}
      {netExposures && (
        <>
          <div style={{
            fontSize: '0.58rem', fontWeight: '800',
            color: '#37474f', padding: '4px 2px'
          }}>
            📊 Exposure
          </div>

          <div style={{
            gridColumn: '2 / -1',
            display: 'flex',
            gap: '12px',
            padding: '8px 12px',
            background: 'linear-gradient(90deg, rgba(33,150,243,0.08), rgba(156,39,176,0.08))',
            borderRadius: '6px',
            border: '1px solid rgba(33,150,243,0.2)'
          }}>
            {/* Normalized Delta */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '5px',
              border: '1px solid #2196f3'
            }}>
              <span style={{
                fontSize: '0.65rem',
                fontWeight: '700',
                color: '#1976d2'
              }}>
                Net Δ exposure:
              </span>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '900',
                color: netExposures.normalized_delta > 0 ? '#00c853' : '#f44336'
              }}>
                {netExposures.normalized_delta > 0 ? '+' : ''}{netExposures.normalized_delta.toLocaleString()}
              </span>
              <span style={{
                fontSize: '0.6rem',
                fontWeight: '700',
                color: netExposures.delta_change_pct > 0 ? '#4CAF50' : '#f44336'
              }}>
                {netExposures.delta_change_pct > 0 ? '+' : ''}{netExposures.delta_change_pct}%
              </span>
            </div>

            {/* Net Gamma */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '5px',
              border: '1px solid #9c27b0'
            }}>
              <span style={{
                fontSize: '0.65rem',
                fontWeight: '700',
                color: '#7b1fa2'
              }}>
                Net Γ:
              </span>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: '900',
                color: netExposures.net_gamma > 0 ? '#00c853' : '#f44336'
              }}>
                {netExposures.net_gamma > 0 ? '+' : ''}{netExposures.net_gamma.toLocaleString()}
              </span>
              <span style={{
                fontSize: '0.6rem',
                fontWeight: '700',
                color: netExposures.gamma_change_pct > 0 ? '#4CAF50' : '#f44336'
              }}>
                {netExposures.gamma_change_pct > 0 ? '+' : ''}{netExposures.gamma_change_pct}%
              </span>
            </div>
          </div>
        </>
      )}



      {/* Grid: Category | Top1-5 | Rank Rot. | Expansion | Target | D-1 | D-2 | D-3 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '70px 1fr 48px 52px 52px 80px 76px 76px 76px',
        gap: '6px',
        alignItems: 'center'
      }}>

        {/* Header */}
        <div style={{ fontWeight: '700', color: '#555', fontSize: '0.65rem' }}>Category</div>
        <div style={{ fontWeight: '700', color: '#555', fontSize: '0.65rem', display: 'flex', justifyContent: 'space-around' }}>
        <span>Top 1</span>
        <span>Top 2</span>
        <span>Top 3</span>
        <span>Top 4</span>
        <span>Top 5</span>
        </div>
        <div style={{ fontWeight: '700', color: '#00897b', textAlign: 'center', fontSize: '0.62rem', lineHeight: '1.3' }}>Trend</div>
        <div style={{ fontWeight: '700', color: '#2196f3', textAlign: 'center', fontSize: '0.62rem', lineHeight: '1.3' }}>Rank<br/>Rot.</div>
        <div style={{ fontWeight: '700', color: '#9c27b0', textAlign: 'center', fontSize: '0.62rem', lineHeight: '1.3' }}>Expan-<br/>sion</div>
        <div style={{ fontWeight: '700', color: '#e65100', textAlign: 'center', fontSize: '0.62rem', lineHeight: '1.3' }}>Target</div>
        <div style={{ fontWeight: '700', color: '#555', textAlign: 'center', fontSize: '0.65rem' }}>D-1</div>
        <div style={{ fontWeight: '700', color: '#555', textAlign: 'center', fontSize: '0.65rem' }}>D-2</div>
        <div style={{ fontWeight: '700', color: '#555', textAlign: 'center', fontSize: '0.65rem' }}>D-3</div>

        {/* Data Rows */}
        {categories.map(({ key, label, builderData, type, regimeKey }) => {
          const hasBuilders = builderData && builderData.length > 0;
          const regimeDays = regimeAnalysis?.[regimeKey];

          return (
            <React.Fragment key={key}>
              <div style={{ fontWeight: '800', color: '#333', fontSize: '0.75rem' }}>{label}</div>

              {/* Top 1-5 builder cells */}
              <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                {hasBuilders ? (
                  (() => {
                    const builders = builderData.slice(0, 5);
                    
                    // Find highest momentum index
                    let highlightIdx = 0;
                    let maxMomentum = builders[0]?.rel_momentum || -Infinity;
                    builders.forEach((b, i) => {
                      if ((b.rel_momentum || -Infinity) > maxMomentum) {
                        maxMomentum = b.rel_momentum;
                        highlightIdx = i;
                      }
                    });
                    
                    // If builders 2-5 all have momentum < 0, force highlight to rank 1
                    if (builders.length >= 2) {
                      const rest = builders.slice(1, 5);
                      if (rest.every(b => (b.rel_momentum || 0) < 0)) {
                        highlightIdx = 0;
                      }
                    }
                    
                    return builders.map((builder, idx) => {
                      let barColor;
                      if      (type === 'OI')       barColor = builder.side === 'CE' ? '#f44336' : '#4CAF50';
                      else if (type === 'VOL')      barColor = builder.side === 'CE' ? '#4CAF50' : '#f44336';
                      else if (type === 'OI_DELTA') barColor = builder.side === 'CE' ? '#ff9800' : '#00bcd4';
                      else if (type === 'OI_VEGA')  barColor = builder.side === 'CE' ? '#9c27b0' : '#4caf50';
                      else if (type === 'OI_GAMMA') barColor = builder.side === 'CE' ? '#ff5722' : '#2196f3';

                      const { icon, color } = getMomentumMeta(builder.rel_momentum);
                      const isExtra = idx >= 5;
                      const isHighlighted = idx === highlightIdx;

                      return (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            padding: '2px 3px',
                            background: isHighlighted ? 'rgba(255, 215, 0, 0.3)' : (isExtra ? 'rgba(160,160,160,0.15)' : 'rgba(255,255,255,0.6)'),
                            borderRadius: '4px',
                            flex: 1,
                            border: isHighlighted ? '2px solid #FFD700' : (isExtra ? '1px dashed rgba(0,0,0,0.3)' : '1px solid rgba(0,0,0,0.05)'),
                            opacity: isExtra ? 0.8 : 1,
                            boxShadow: isHighlighted ? '0 0 8px rgba(255, 215, 0, 0.4)' : 'none'
                          }}
                        >
                          {/* Strike + side */}
                          <span style={{ fontSize: isExtra ? '0.75rem' : '0.75rem', fontWeight: '800', color: '#333', whiteSpace: 'nowrap' }}>
                          {hasStrikeBeenTouched(builder.strike) ? '⚪': builder.side === 'CE' ? '🟢' : '🔴'} {builder.strike} {builder.side}
                          </span>

                          {/* Rank-based duration — only for top 3 */}
                          {!isExtra && builder.duration_minutes > 0 && (
                            <span
                              style={{ fontSize: '0.46rem', color: '#666', fontWeight: '600', whiteSpace: 'nowrap' }}
                              title={`At this rank for ${builder.duration_minutes} min`}
                            >
                              {builder.duration_minutes}m
                            </span>
                          )}

                          {/* Mini bar */}
                          <div style={{ width: '14px', height: '3px', background: 'rgba(0,0,0,0.1)', borderRadius: '2px', overflow: 'hidden', flexShrink: 0 }}>
                            <div style={{ width: `${builder.relative_pct || 0}%`, height: '100%', background: barColor }} />
                          </div>

                          {/* Relative % */}
                          <span style={{ fontSize: '0.65rem', fontWeight: '700', color: barColor, whiteSpace: 'nowrap' }}>
                            {builder.relative_pct}%
                          </span>

                          {/* Momentum */}
                          <span style={{ fontSize: '0.65rem', fontWeight: '700', color, whiteSpace: 'nowrap' }}>
                            {icon}{Math.abs(builder.rel_momentum || 0).toFixed(1)}
                          </span>

                          {/* Previous rank */}
                          {builder.prev_ranks?.length > 0 && (
                            <span style={{ fontSize: '0.70rem', color: '#444', fontWeight: '600',
                              whiteSpace: 'nowrap', background: 'rgba(0,0,0,0.05)',
                              borderRadius: '3px', padding: '1px 3px' }}>
                              {builder.prev_ranks.map(r => `${r}`).join('<')}
                            </span>
                          )}

                        </div>
                      );
                    });
                  })()
                ) : (
                  <div style={{ fontSize: '0.6rem', color: '#999', fontStyle: 'italic' }}>No data</div>
                )}


              </div>
              {renderTrend(regimeDays)}
              {renderRankRotation(regimeDays)}
              {renderExpansion(regimeDays)}
              {renderTarget(regimeDays, builderData)}
              {renderRegimeDay(regimeDays?.day_minus_1)}
              {renderRegimeDay(regimeDays?.day_minus_2)}
              {renderRegimeDay(regimeDays?.day_minus_3)}
            </React.Fragment>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        marginTop: '10px', padding: '8px',
        background: 'rgba(255,255,255,0.5)', borderRadius: '6px',
        fontSize: '0.55rem', color: '#666',
        display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '8px'
      }}>
        <span><strong>Top 4:</strong> Display only – not in analysis</span>
        <span><strong style={{color:'#2196f3'}}>▲▼ Rank Rot.:</strong> Persistent signal – higher/lower strike took #1</span>
        <span><strong style={{color:'#9c27b0'}}>▲▼ Expansion:</strong> Persistent signal – higher/lower strike entered top 3</span>
        <span><strong style={{color:'#e65100'}}>Target:</strong> Best top-3 strike ≥60% in signal direction</span>
        <span><strong>Duration:</strong> Time at current rank (resets on rank change)</span>
        <span><strong>D-1/D-2/D-3:</strong> Strike + side only</span>
      </div>
    </div>
  );
};


const VolumeBuilders = ({ buildersData }) => {
  if (!buildersData || !buildersData.vol_builders?.length) {
    return null;
  }

  const { vol_builders = [] } = buildersData;

  const getMomentumMeta = (v = 0) => {
    if (v > 0.3) return { icon: '▲', color: '#4CAF50' };
    if (v < -0.3) return { icon: '▼', color: '#f44336' };
    return { icon: '•', color: '#aaa' };
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '4px 10px',
      background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
      borderRadius: '6px',
      border: '1.5px solid #4CAF50'
    }}>
      <div style={{
        fontSize: '0.65rem',
        fontWeight: '700',
        color: '#2e7d32'
      }}>
        📊 Vol
      </div>

      {vol_builders.slice(0, 3).map((builder, index) => {
        // VOL: CE=green, PE=red
        const barColor = builder.side === 'CE' ? '#4CAF50' : '#f44336';
        const { icon, color } = getMomentumMeta(builder.rel_momentum);

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {/* RANK 
            <span style={{
              fontSize: '0.6rem',
              fontWeight: '700',
              color: '#666'
            }}>
              #{index + 1}
            </span> */}

            {/* STRIKE + SIDE */}
            <span style={{
              fontSize: '0.65rem',
              fontWeight: '800',
              color: '#333'
            }}>
              {builder.strike} {builder.side}
            </span>

            {/* MINI BAR */}
            <div style={{
              width: '20px',
              height: '3px',
              background: 'rgba(0,0,0,0.1)',
              borderRadius: '2px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${builder.relative_pct || 0}%`,
                height: '100%',
                background: barColor
              }} />
            </div>

            {/* PERCENTAGE */}
            <span style={{
              fontSize: '0.6rem',
              fontWeight: '800',
              color: barColor
            }}>
              {builder.relative_pct ?? 0}%
            </span>

            {/* MOMENTUM */}
            <span style={{
              fontSize: '0.55rem',
              fontWeight: '700',
              color
            }}>
              {icon}{Math.abs(builder.rel_momentum || 0).toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
};


// Action Leaders Direct Display Component
const ActionLeadersDisplay = ({ actionLeaders }) => {
  if (!actionLeaders) {
    return null;
  }

  // Category metadata
  const categoryMeta = {
    volume: { label: 'Vol', color: '#4CAF50', icon: '🔥' },
    oi: { label: 'OI', color: '#2196f3'},
    oi_delta: { label: 'OIΔ', color: '#ff9800' },
    oi_vega: { label: 'OIV', color: '#9c27b0'},
    oi_gamma: { label: 'OIG', color: '#f44336'}
  };

  // Get active leaders (non-null)
  const activeLeaders = Object.entries(actionLeaders).filter(([_, data]) => data !== null);

  if (activeLeaders.length === 0) {
    return null;
  }

  const getColorBySide = (side) => side === 'CE' ? '#f44336' : '#4CAF50';
  
  const getDurationColor = (min) => {
    if (min < 2) return '#ff9800';
    if (min < 5) return '#ffc107';
    return '#00c853';
  };

  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      flexWrap: 'wrap'
    }}>

      {/* Leader Cards */}
      {activeLeaders.map(([category, data]) => {
        const meta = categoryMeta[category];
        
        return (
          <div
            key={category}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 2px',
              background: `linear-gradient(135deg, ${meta.color}15, ${meta.color}08)`,
              borderRadius: '3px',
              border: `1.0px solid ${meta.color}`,
              boxShadow: `0 2px 2px ${meta.color}20`
            }}
          >
            {/* Category Icon + Label */}
            <div style={{
              fontSize: '0.6rem',
              fontWeight: '800',
              color: meta.color,
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              <span style={{ fontSize: '0.75rem' }}>{meta.icon}</span>
              <span>{meta.label}</span>
            </div>

            {/* Vertical Separator */}
            <div style={{
              width: '1px',
              height: '20px',
              background: `${meta.color}40`
            }} />

            {/* Strike + Side */}
            <div style={{
              fontSize: '0.70rem',
              fontWeight: '900',
              color: getColorBySide(data.side),
              minWidth: '50px',
              textAlign: 'center'
            }}>
              {data.strike} {data.side}
            </div>

            {/* Weight Badge */}
            <div style={{
              fontSize: '0.6rem',
              fontWeight: '800',
              color: meta.color,
              minWidth: '25px',
              textAlign: 'center'
            }}>
              {data.weight_pct}%
            </div>

            {/* Duration */}
            <div style={{
              fontSize: '0.6rem',
              fontWeight: '700',
              color: meta.color,
              minWidth: '25px',
              textAlign: 'left',
            }}>
              {data.duration_min.toFixed(1)}m
            </div>
          </div>
        );
      })}
    </div>
  );
};



// PatternIntelligence - Tooltip style (like Top Builders)

const PatternIntelligence = ({ symbol, spotPrice }) => {
  const [intel, setIntel] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    fetch(`/api/intelligence/${symbol}`)
      .then((res) => res.json())
      .then((data) => setIntel(data))
      .catch(() => setIntel(null));
  }, [symbol]);

  const STRIKE_STEP = {
    NIFTY: 50,
    FINNIFTY: 50,
    BANKNIFTY: 100,
    SENSEX: 100,
  };

  // Calculate pattern data
  const hasPattern = intel && intel.known_pattern;
  
  let targetStrike = null;
  let probability = 0;
  let direction = null;

  if (hasPattern) {
    const move = intel.expected_favorable_excursion || 0;
    direction = intel.avg_move_pct < 0 ? 'DOWN' : 'UP';
    const directionMultiplier = intel.avg_move_pct < 0 ? -1 : 1;
    const rawTarget = spotPrice + directionMultiplier * move;
    const step = STRIKE_STEP[symbol] || 50;
    targetStrike = Math.round(rawTarget / step) * step;
    probability = Math.round((intel.breakout_probability || 0) * 100);
  }

  // Determine icon and color
  const statusIcon = hasPattern ? '🎯' : '❓';
  const statusColor = hasPattern ? '#38bdf8' : '#999';

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.7rem',
        fontWeight: '700',
        color: statusColor,
        cursor: 'pointer',
        userSelect: 'none'
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {statusIcon} Pattern

      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px',
            minWidth: '240px',
            padding: '14px',
            background: 'rgba(0,0,0,0.95)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            zIndex: 1000
          }}
        >
          {!hasPattern ? (
            // No pattern detected
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: '700',
                color: '#fff',
                marginBottom: '8px'
              }}>
                🎯 Pattern Analysis
              </div>
              <div style={{
                fontSize: '0.65rem',
                color: '#999',
                lineHeight: '1.4'
              }}>
                No known pattern detected or insufficient historical data for this setup.
              </div>
            </div>
          ) : (
            // Pattern detected
            <div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: '700',
                color: '#38bdf8',
                marginBottom: '10px',
                paddingBottom: '6px',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center'
              }}>
                🎯 Pattern Target
              </div>

              {/* Target Strike */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px',
                background: 'rgba(56,189,248,0.15)',
                borderRadius: '6px',
                border: '1px solid rgba(56,189,248,0.3)'
              }}>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: '#aaa'
                }}>
                  Target Strike:
                </span>
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '900',
                  color: '#38bdf8'
                }}>
                  {targetStrike}
                </span>
              </div>

              {/* Probability */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px',
                background: 'rgba(56,189,248,0.1)',
                borderRadius: '6px'
              }}>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: '#aaa'
                }}>
                  Probability:
                </span>
                <span style={{
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  color: probability >= 70 ? '#00c853' : probability >= 50 ? '#ff9800' : '#f44336'
                }}>
                  {probability}%
                </span>
              </div>

              {/* Direction */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px',
                background: 'rgba(56,189,248,0.1)',
                borderRadius: '6px'
              }}>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: '#aaa'
                }}>
                  Direction:
                </span>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '800',
                  color: direction === 'UP' ? '#00c853' : '#f44336'
                }}>
                  {direction === 'UP' ? '⬆️ BULLISH' : '⬇️ BEARISH'}
                </span>
              </div>

              {/* Footer note */}
              <div style={{
                fontSize: '0.5rem',
                color: '#777',
                textAlign: 'center',
                marginTop: '10px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                fontStyle: 'italic'
              }}>
                Based on historical pattern analysis
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const IndexRow = ({ symbol, displayName, data, loading, lastUpdate, liveSpot, liveOptions, selectedModules  }) => {

  if (!data) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        background: 'rgba(0,0,0,0.03)',
        borderRadius: '8px',
        margin: '8px 0'
      }}>
        <div style={{ color: '#999', fontSize: '0.9rem' }}>
          {loading ? '⏳ Loading...' : '❌ No Data'}
        </div>
      </div>
    );
  }

  // Extract data from API response
  const { sr_levels, spot_price, ob_os, bias_data, futures_data } = data;
  const liveSymbolData = liveSpot[symbol];
  // 🔥 Use FAST options feed only
  const optionsData = liveOptions[symbol] || {};

  
  // ✅ Futures data with live LTP overlay (if available from WebSocket)
  const liveFuturesData = liveSymbolData?.futures?.ltp 
    ? {
        ...futures_data,
        ltp: liveSymbolData.futures.ltp,
        ohlc: liveSymbolData.futures.ohlc || futures_data?.ohlc
      }
    : futures_data;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
      background: 'linear-gradient(135deg, #fafafa, #f5f5f5)',
      borderRadius: '12px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      margin: '12px 0',
      border: '1px solid #e0e0e0'
    }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}
      >

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <span
            style={{
              fontSize: '0.8rem',
              fontWeight: '700',
              color: '#444'
            }}
          >
            {displayName}
          </span>

          <VolumeBuilders buildersData={data.builders_data} />          
           
          <ActionLeadersDisplay actionLeaders={data.action_leaders} />
         
        {/*  <PatternIntelligence symbol={symbol} spotPrice={spot_price} /> */}

        </div>

        {/* RIGHT SIDE: Updated time */}
        {lastUpdate && (
          <span
            style={{
              fontSize: '0.65rem',
              color: '#999'
            }}
          >
            {lastUpdate}
          </span>
        )}

      </div>
      
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'wrap'
      }}>

        {/* S/R Level Tiles */}
        <LevelTile
          label="S-Ext"
          arrow={sr_levels.s_ext?.arrow}
          strike={sr_levels.s_ext?.strike}
          side={sr_levels.s_ext?.side}
          strengthPct={sr_levels.s_ext?.strength_pct || 0}
          remainingIndicator={sr_levels.s_ext?.remaining_indicator}
          crownPcts={sr_levels.s_ext?.crown_pcts}
          normPcts={sr_levels.s_ext?.norm_pcts}
          color="#ff5252"
          bgGradient="linear-gradient(180deg, #fff5f5, #ffebee)"
        />
        
        <LevelTile
          label="Support"
          arrow={sr_levels.support?.arrow}
          strike={sr_levels.support?.strike}
          side={sr_levels.support?.side}
          strengthPct={sr_levels.support?.strength_pct || 0}
          remainingIndicator={sr_levels.support?.remaining_indicator}
          crownPcts={sr_levels.support?.crown_pcts}
          normPcts={sr_levels.support?.norm_pcts}
          color="#00c853"
          bgGradient="linear-gradient(180deg, #f1f8e9, #dcedc8)"
        />
        
        <LevelTile
          label="Resistance"
          arrow={sr_levels.resistance?.arrow}
          strike={sr_levels.resistance?.strike}
          side={sr_levels.resistance?.side}
          strengthPct={sr_levels.resistance?.strength_pct || 0}
          remainingIndicator={sr_levels.resistance?.remaining_indicator}
          crownPcts={sr_levels.resistance?.crown_pcts}
          normPcts={sr_levels.resistance?.norm_pcts}
          color="#d32f2f"
          bgGradient="linear-gradient(180deg, #ffebee, #ffcdd2)"
        />
        
        <LevelTile
          label="R-Ext"
          arrow={sr_levels.r_ext?.arrow}
          strike={sr_levels.r_ext?.strike}
          side={sr_levels.r_ext?.side}
          strengthPct={sr_levels.r_ext?.strength_pct || 0}
          remainingIndicator={sr_levels.r_ext?.remaining_indicator}
          crownPcts={sr_levels.r_ext?.crown_pcts}
          normPcts={sr_levels.r_ext?.norm_pcts}
          color="#ff1744"
          bgGradient="linear-gradient(180deg, #fff5f5, #ffebee)"
        />

        {/* Underlying Spot Tile */}
        {selectedModules.includes("underlying") && (
        <UnderlyingTile
            name={displayName}
            price={spot_price}
            loading={false}
            obOs={ob_os}
            expiry={data.expiry_display}
            liveSpotData={liveSymbolData}
            biasData={bias_data}
            spotChangePct={data.spot_change_pct}
            directionPredictor={data.direction_predictor}
        />
        )}

        {/* Options LTP Tile - Backend provides change_pct */}
        {selectedModules.includes("optionLTP") && (
        <OptionLTPTile
            name={displayName}
            optionsData={optionsData}
            loading={false}
        />
        )}

        {/* Futures Tile */}
        <FutureTile
          name={symbol}
          futuresData={data?.futures_data}
          loading={loading}
        />

      </div>

      {/* PCR Analysis Section */}
        {selectedModules.includes("pcr") && (
        <PCRAnalysisRow pcrData={data.pcr_analysis} />
        )}

        {selectedModules.includes("builders") && (
        <TopBuildersTile 
            buildersData={data.builders_data} 
            regimeAnalysis={data.regime_analysis}
            spotOhlc={liveSymbolData?.spot?.ohlc || data?.futures_data?.ohlc}
            spotPrice={liveSymbolData?.spot?.ltp || data?.spot_price || 0}
            netExposures={data.net_exposures}
        />
        )}
    </div>
  );
};

// Settings Sidebar Component
const SettingsSidebar = ({ isOpen, onClose, selectedIndices, onSelectionChange, refreshInterval, selectedModules, setSelectedModules, isDashboard }) => {
  const allIndices = [
    { id: 'NIFTY', name: 'Nifty 50', icon: '📈' },
    { id: 'SENSEX', name: 'BSE Sensex', icon: '📊' },
    { id: 'BANKNIFTY', name: 'Bank Nifty', icon: '🏦' },
    { id: 'FINNIFTY', name: 'Fin Nifty', icon: '💰' }
  ];
  

  const [tempSelection, setTempSelection] = useState(selectedIndices);
  
  useEffect(() => {
    setTempSelection(selectedIndices);
  }, [selectedIndices]);
  
  const toggleIndex = (indexId) => {
    if (tempSelection.includes(indexId)) {
      if (tempSelection.length > 1) {
        setTempSelection(tempSelection.filter(id => id !== indexId));
      }
    } else {
      setTempSelection([...tempSelection, indexId]);
    }
  };
  
  const handleApply = () => {
    onSelectionChange(tempSelection);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 999,
          animation: 'fadeIn 0.2s ease-in-out'
        }}
        onClick={onClose}
      />
      
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '320px',
        height: '100vh',
        background: 'linear-gradient(180deg, #ffffff, #f8f9fa)',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '2px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea, #764ba2)'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.3rem',
            fontWeight: '800',
            color: '#fff'
          }}>
            ⚙️ Dashboard Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              transition: 'all 0.2s'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          <div style={{
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '0.9rem',
              fontWeight: '700',
              color: '#666',
              marginBottom: '12px',
              textTransform: 'uppercase'
            }}>
              Select Indices
            </h3>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {allIndices.map(index => (
                <div
                  key={index.id}
                  onClick={() => toggleIndex(index.id)}
                  style={{
                    padding: '14px 16px',
                    background: tempSelection.includes(index.id) 
                      ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                      : '#fff',
                    border: tempSelection.includes(index.id)
                      ? '2px solid #667eea'
                      : '2px solid #e0e0e0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s',
                    boxShadow: tempSelection.includes(index.id)
                      ? '0 4px 12px rgba(102,126,234,0.3)'
                      : 'none'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '1.3rem' }}>{index.icon}</span>
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      color: tempSelection.includes(index.id) ? '#fff' : '#333'
                    }}>
                      {index.name}
                    </span>
                  </div>
                  
                  {tempSelection.includes(index.id) && (
                    <Check size={20} color="#fff" strokeWidth={3} />
                  )}
                </div>
              ))}
            </div>
            
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: 'rgba(102,126,234,0.1)',
              borderRadius: '6px',
              fontSize: '0.75rem',
              color: '#666',
              textAlign: 'center'
            }}>
              ℹ️ At least one index must be selected
            </div>
          </div>
          
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            borderRadius: '10px',
            border: '2px solid #2196f3'
          }}>
            <h4 style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              color: '#1976d2',
              marginBottom: '8px'
            }}>
              📡 Refresh Information
            </h4>
            <div style={{
              fontSize: '0.75rem',
              color: '#555',
              lineHeight: '1.5'
            }}>
              <div>Selected: <strong>{tempSelection.length} {tempSelection.length === 1 ? 'index' : 'indices'}</strong></div>
              <div>Per-index interval: <strong>5 seconds</strong></div>
              <div>Full cycle: <strong>{tempSelection.length * 5} seconds</strong></div>
              <div style={{ marginTop: '8px', fontSize: '0.7rem', opacity: 0.8 }}>
                Each index updates every {tempSelection.length * 5}s (rotating 5s interval)
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ marginTop: "16px" }}>
        <div style={{
            fontSize: "0.75rem",
            fontWeight: "700",
            marginBottom: "8px"
        }}>
            Select Modules
        </div>

        {ANALYTICS_MODULES.map(mod => (
            <label key={mod.id} style={{ display: "block", marginBottom: "6px" }}>
            <input
                type="checkbox"
                checked={selectedModules.includes(mod.id)}
                onChange={() => {
                setSelectedModules(prev =>
                    prev.includes(mod.id)
                    ? prev.filter(m => m !== mod.id)
                    : [...prev, mod.id]
                );
                }}
            />
            {" "}{mod.label}
            </label>
        ))}
        </div>

        <div style={{
          padding: '20px',
          borderTop: '2px solid #e0e0e0',
          background: '#fff'
        }}>
          <button
            onClick={handleApply}
            disabled={tempSelection.length === 0}
            style={{
              width: '100%',
              padding: '14px',
              background: tempSelection.length > 0 
                ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                : '#ccc',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: tempSelection.length > 0 ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              boxShadow: tempSelection.length > 0 
                ? '0 4px 12px rgba(102,126,234,0.3)' 
                : 'none'
            }}
          >
            ✓ Apply Settings
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};


function EmptyState({ onConfigure }) {
  return (
    <div style={{
      textAlign: "center",
      padding: "80px 20px",
      color: "#444"
    }}>
      <div style={{
        fontSize: "2rem",
        fontWeight: "800",
        marginBottom: "12px"
      }}>
        👋 Welcome to My Dashboard
      </div>

      <div style={{
        fontSize: "0.9rem",
        color: "#666",
        marginBottom: "24px"
      }}>
        Build your custom trading workspace by selecting indices and analytics modules
      </div>

      <button
        onClick={onConfigure}
        style={{
          padding: "12px 24px",
          background: "linear-gradient(135deg, #667eea, #764ba2)",
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontSize: "0.9rem",
          fontWeight: "700",
          cursor: "pointer"
        }}
      >
        ⚙️ Configure Dashboard
      </button>
    </div>
  );
}

export default function AnalyticsPage() {

    const isMobile = useIsMobile();

  // ✅ MODULE STATE (your new addition)
    const [singleModules, setSingleModules] = useState([
    "underlying",
    "optionLTP",
    "pcr",
    "builders"
    ]);

    const [compareModules, setCompareModules] = useState([ ]);

    const { index } = useParams();
    const isComparePage = !index;

    const activeModules = isComparePage ? compareModules : singleModules;
    const setActiveModules = isComparePage ? setCompareModules : setSingleModules;



    // ✅ ORIGINAL STATES (cleaned)
  const [data, setData] = useState({
    NIFTY: null,
    BANKNIFTY: null,
    FINNIFTY: null,
    SENSEX: null 
  });
  
  const [loading, setLoading] = useState({
    NIFTY: false,
    BANKNIFTY: false,
    FINNIFTY: false,
    SENSEX: false
  });
  
  const [lastUpdate, setLastUpdate] = useState({
    NIFTY: null,
    BANKNIFTY: null,
    FINNIFTY: null,
    SENSEX: null
  });
  
  const [liveSpot, setLiveSpot] = useState({
    NIFTY: null,
    BANKNIFTY: null,
    FINNIFTY: null,
    SENSEX: null
  });


    const [selectedIndices, setSelectedIndices] = useState(
    index ? [index.toUpperCase()] : [ ]
    );

    const isMyDashboard = isComparePage;

    const hasDashboard =
    selectedIndices.length > 0 && activeModules.length > 0;

    useEffect(() => {
    if (!isComparePage && index) {
        setSelectedIndices(prev => {
        // ⚠️ only override if NOT coming from dashboard
        if (prev.length > 1) return prev;
        return [index.toUpperCase()];
        });
    }
    }, [index, isComparePage]);

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
    if (isMyDashboard && !isInitialized) {
        setSelectedIndices([]);
        setActiveModules([]);
        setIsInitialized(true);
    }
    }, [isMyDashboard, isInitialized]);

    const dashboardRef = React.useRef({
    indices: [],
    modules: []
    });

    useEffect(() => {
    if (isComparePage) {
        dashboardRef.current = {
        indices: selectedIndices,
        modules: compareModules
        };
    }
    }, [selectedIndices, compareModules, isComparePage]);

    useEffect(() => {
    if (isComparePage) {
        if (dashboardRef.current.indices.length > 0) {
        setSelectedIndices(dashboardRef.current.indices);
        setCompareModules(dashboardRef.current.modules);
        }
    }
    }, [isComparePage]);


  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentFetch, setCurrentFetch] = useState(0);
  const [liveOptions, setLiveOptions] = useState({});

  const indexMetadata = {
    'NIFTY': { name: 'Nifty 50', icon: '📈' },
    'SENSEX': { name: 'BSE Sensex', icon: '📊' },
    'BANKNIFTY': { name: 'Bank Nifty', icon: '🏦' },
    'FINNIFTY': { name: 'Fin Nifty', icon: '💰' }
  };

  // ✅ FUNCTIONS
  const fetchData = async (symbol) => {
    setLoading(prev => ({ ...prev, [symbol]: true }));
    
    try {
      const response = await fetch(`${API_BASE}/sr-levels/${symbol}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setData(prev => ({ ...prev, [symbol]: result }));
        setLastUpdate(prev => ({
          ...prev,
          [symbol]: new Date().toLocaleTimeString()
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${symbol}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [symbol]: false }));
    }
  };

  // ✅ FAST LTP
  useEffect(() => {
    let inFlight = false;

    const fetchFastData = async () => {
      if (inFlight) return;
      inFlight = true;

      try {
        const response = await fetch(`${API_BASE}/live-ltp`);
        const result = await response.json();
        if (result.status === 'success') {
          setLiveSpot(result.data);
        }
      } catch (error) {
        console.error('Fast LTP fetch error:', error);
      } finally {
        inFlight = false;
      }
    };

    fetchFastData();
    const interval = setInterval(fetchFastData, 1000);
    return () => clearInterval(interval);
  }, []);

  // ✅ OPTIONS LTP
  useEffect(() => {
    const fetchOptions = async () => {
      const updates = {};

      for (const symbol of selectedIndices) {
        const res = await fetch(`${API_BASE}/options-ltp/${symbol}`);
        const json = await res.json();

        if (json.status === 'success') {
          updates[symbol] = json.options_ltp_data;
        }
      }

      setLiveOptions(prev => ({ ...prev, ...updates }));
    };

    fetchOptions();
    const id = setInterval(fetchOptions, 1000);
    return () => clearInterval(id);
  }, [selectedIndices]);

  // ✅ ROTATING FETCH
  useEffect(() => {
    selectedIndices.forEach(symbol => fetchData(symbol));

    const interval = setInterval(() => {
      setCurrentFetch(prev => {
        const next = (prev + 1) % selectedIndices.length;
        const symbol = selectedIndices[next];
        fetchData(symbol);
        return next;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedIndices]);

    // ================================
    // INSERT MOBILE CHECK HERE
    // ================================

    if (isMobile) {
      return (
        <MobileDashboard
          data={data}
          loading={loading}
          lastUpdate={lastUpdate}
          liveSpot={liveSpot}
          liveOptions={liveOptions}
          selectedIndices={selectedIndices}
          setSelectedIndices={setSelectedIndices}
          indexMetadata={indexMetadata}
          activeModules={activeModules}
          setActiveModules={setActiveModules}
        />
      );
    }


  // ✅ UI ONLY
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif"
      }}
    >
        <SettingsSidebar
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        selectedIndices={selectedIndices}
        onSelectionChange={setSelectedIndices}
        refreshInterval={selectedIndices.length * 5}
        selectedModules={activeModules}
        setSelectedModules={setActiveModules}
        isDashboard={isComparePage}   // ✅ NEW PROP
        />


      <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
<div
  style={{
    background: 'linear-gradient(135deg, #ffffff, #f5f5f5)',
    padding: '16px 20px',
    borderRadius: '16px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }}
>

  {/* 🔵 LEFT → LOGO */}
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: "180px"
  }}>
    {/* 👉 REPLACE THIS WITH YOUR LOGO */}
    <div
    style={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
    }}
    >
    <RozoQuantLogo width={160} height={45} />
    </div>
  </div>


  {/* 🟣 CENTER → NAVIGATION */}
  <div style={{
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flex: 1
  }}>
    {[
      { label: "MY DASHBOARD", path: "/compare" },
      { label: "NIFTY", path: "/nifty" },
      { label: "BANKNIFTY", path: "/banknifty" },
      { label: "SENSEX", path: "/sensex" },
      { label: "FINNIFTY", path: "/finnifty" },
      { label: "GEX/DEX", path: "/gex-dex" }
    ].map(item => (
      <NavLink
        key={item.path}
        to={item.path}
        style={({ isActive }) => ({
          padding: '8px 16px',
          borderRadius: '8px',
          textDecoration: 'none',
          fontSize: '0.8rem',
          fontWeight: '700',
          background: isActive
            ? 'linear-gradient(135deg, #667eea, #764ba2)'
            : '#eee',
          color: isActive ? '#fff' : '#333'
        })}
      >
        {item.label}
      </NavLink>
    ))}
  </div>


  {/* 🟢 RIGHT → SETTINGS (future: profile here) */}
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: "180px",
    justifyContent: "flex-end"
  }}>
    <button
      onClick={() => setSettingsOpen(true)}
      title="Dashboard Settings"
      style={{
        fontSize: "1.3rem",
        background: "rgba(102,126,234,0.15)",
        border: "none",
        borderRadius: "8px",
        padding: "6px 10px",
        cursor: "pointer",
        color: "#333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(102,126,234,0.3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(102,126,234,0.15)";
      }}
    >
      ⚙️
    </button>
  </div>

</div>


        <div
          style={{
            background: 'rgba(255,255,255,0.95)',
            padding: '24px',
            borderRadius: '16px'
          }}
        >
        {isMyDashboard && !hasDashboard ? (
        <EmptyState onConfigure={() => setSettingsOpen(true)} />
        ) : (
        selectedIndices.map(symbol => (
            <IndexRow
            key={symbol}
            symbol={symbol}
            displayName={indexMetadata[symbol].name}
            data={data[symbol]}
            loading={loading[symbol]}
            lastUpdate={lastUpdate[symbol]}
            liveSpot={liveSpot}
            liveOptions={liveOptions}
            selectedModules={activeModules}
            />
        ))
        )}
        </div>
      </div>
    </div>
  );
}

